import {
  ChevronDoubleRightIcon,
  PhoneIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline'
import { PencilSquareIcon, TicketIcon } from '@heroicons/react/24/solid'
import { Transition } from '@headlessui/react'
import { Tooltip } from 'flowbite-react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useAppSelector, useAppDispatch } from '../../../shared/redux/hooks'
import { NOTES_DRAWER_WIDTH_KEY, useResizableDrawerWidth } from '../../../shared/useResizableDrawerWidth'
import {
  setDockedDrawerState,
  useDockedDrawerRightOffset,
} from '../../../shared/dockedDrawerLayout'
import { updateNote, addNote, deleteNote, getNotes } from '../Notes.slice'
import { clearCalls, getCalls } from '../Calls.slice'
import { getTickets } from '../Tickets.slice'
import Modal from '../../../components/Modal'
import { Note } from '../../../apis/types'
import Paginator from '../../../components/Table/Paginator'
import PanelNotes from './NotesPanel'
import CallsPanel from './CallsPanel'
import TicketsPanel from './TicketsPanel'
import { useNotesViewContext, setNotesViewContext } from '../notesViewContext'
import { isNotesCapableRoute } from '../notesRoute'

const DOCK_STORAGE_KEY = 'notesDrawerDocked'
const OPEN_STORAGE_KEY = 'notesDrawerOpen'

const NOTES_TOOLTIP_CLASS =
  'text-xs z-[60] pointer-events-none select-none bg-slate-600 text-white dark:bg-slate-950 border-0'

const NOTES_TOOLTIP_THEME = {
  arrow: {
    style: {
      dark: 'bg-slate-600 dark:bg-slate-950',
      light: 'bg-slate-600 dark:bg-slate-950',
      auto: 'bg-slate-600 dark:bg-slate-950',
    },
  },
}

function readDockedPreference(): boolean {
  try {
    return localStorage.getItem(DOCK_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function readOpenPreference(): boolean {
  try {
    return sessionStorage.getItem(OPEN_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function persistOpenPreference(open: boolean): void {
  try {
    sessionStorage.setItem(OPEN_STORAGE_KEY, open ? '1' : '0')
  } catch {
    // ignore
  }
}

function clearOpenPreference(): void {
  try {
    sessionStorage.removeItem(OPEN_STORAGE_KEY)
  } catch {
    // ignore
  }
}

function formatDrawerTitle(sourceEntity: string): string {
  if (sourceEntity.includes('program') || sourceEntity.includes('property')) {
    return 'Program/Property Notes'
  }
  return `${sourceEntity.split('|')?.[0]?.replace(/\b\w/g, (l) => l.toUpperCase()) ?? 'Record'} Info`
}

const NotesDrawer: React.FC = () => {
  const location = useLocation()
  const context = useNotesViewContext()

  const [open, setOpenState] = useState(() => readOpenPreference())
  const [docked, setDocked] = useState(() => readDockedPreference())
  const dockedRef = useRef(docked)
  dockedRef.current = docked

  const setOpen = (next: boolean): void => {
    setOpenState(next)
    if (dockedRef.current) {
      persistOpenPreference(next)
    } else if (!next) {
      clearOpenPreference()
    }
  }
  const [activeTab, setActiveTab] = useState<'notes' | 'calls' | 'tickets'>('notes')
  const [postNoteBody, setPostNoteBody] = useState<string>('')
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)
  const { user } = useAuth0()
  const { noteRequestPhase, notes, total: notesTotal, resultsPerPage: notesResultsPerPage } = useAppSelector(
    (state) => state.notes
  )
  const { ticketRequestPhase, tickets, total: ticketsTotal, resultsPerPage: ticketsResultsPerPage } = useAppSelector(
    (state) => state.tickets
  )
  const { callRequestPhase, calls, total: callsTotal, resultsPerPage: callsResultsPerPage } = useAppSelector(
    (state) => state.calls
  )
  const dispatch = useAppDispatch()
  const panelRef = useRef<HTMLDivElement>(null)
  const prevSourceEntityRef = useRef<string | undefined>(undefined)
  const { width: drawerWidth, onResizePointerDown } = useResizableDrawerWidth(NOTES_DRAWER_WIDTH_KEY)
  const rightOffset = useDockedDrawerRightOffset('notes')

  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const stored = localStorage.getItem('noteSortDirection')
    return stored === 'asc' || stored === 'desc' ? stored : 'desc'
  })

  const [currentNotesPage, setCurrentNotesPage] = useState(0)
  const [currentCallsPage, setCurrentCallsPage] = useState(0)
  const [currentTicketsPage, setCurrentTicketsPage] = useState(0)
  const [currentSort, setCurrentSort] = useState<'asc' | 'desc'>(sortDirection)

  const prevNotesRouteRef = useRef(isNotesCapableRoute(location.pathname, location.search))
  const onNotesRoute = isNotesCapableRoute(location.pathname, location.search)
  const showDrawerChrome = context != null || (docked && open && onNotesRoute)

  useEffect(() => {
    if (!showDrawerChrome) {
      setDockedDrawerState('notes', { open: false, docked: false, width: 0 })
      return
    }
    setDockedDrawerState('notes', { open, docked, width: drawerWidth })
  }, [showDrawerChrome, open, docked, drawerWidth])

  useEffect(() => {
    const notesRoute = isNotesCapableRoute(location.pathname, location.search)
    const leftNotesRoute = prevNotesRouteRef.current && !notesRoute
    prevNotesRouteRef.current = notesRoute

    if (leftNotesRoute) {
      setNotesViewContext(null)
      setOpen(false)
      clearOpenPreference()
      return
    }

    if (docked && notesRoute && readOpenPreference() && context != null) {
      setOpenState(true)
    }
  }, [location.pathname, location.search, docked, context])

  const displayContext = context
  const sourceEntity = displayContext?.sourceEntity
  const label = displayContext?.label
  const tenantCuid = displayContext?.tenantCuid
  const drawerTab = tenantCuid != null ? activeTab : 'notes'

  useEffect(() => {
    if (tenantCuid == null) {
      setActiveTab('notes')
    }
  }, [tenantCuid])

  useEffect(() => {
    if (context == null) return
    if (prevSourceEntityRef.current !== context.sourceEntity) {
      setCurrentNotesPage(0)
      setCurrentCallsPage(0)
      setCurrentTicketsPage(0)
      dispatch(clearCalls())
      prevSourceEntityRef.current = context.sourceEntity
    }
  }, [context, dispatch])

  useEffect(() => {
    if (sourceEntity == null || sourceEntity.length === 0) return
    dispatch(
      getNotes({
        sourceEntity,
        resultsPerPage: 10,
        page: currentNotesPage,
        sortCreatedAt: currentSort,
      })
    )
  }, [dispatch, sourceEntity, currentNotesPage, currentSort])

  useEffect(() => {
    if (!tenantCuid) return
    dispatch(
      getCalls({
        tenantCuid,
        resultsPerPage: 10,
        page: activeTab === 'calls' ? currentCallsPage : 0,
      })
    )
  }, [dispatch, tenantCuid, activeTab, currentCallsPage])

  useEffect(() => {
    if (!tenantCuid) return
    dispatch(
      getTickets({
        tenantCuid,
        resultsPerPage: 10,
        page: activeTab === 'tickets' ? currentTicketsPage : 0,
      })
    )
  }, [dispatch, tenantCuid, activeTab, currentTicketsPage])

  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    if (panel == null) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (confirmModal) return
      event.preventDefault()
      setOpen(false)
    }
    panel.addEventListener('keydown', onKeyDown)
    return () => panel.removeEventListener('keydown', onKeyDown)
  }, [open, confirmModal])

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (context == null) return
      if (
        event.key === 'n' &&
        !event.ctrlKey &&
        !event.metaKey &&
        !(event.target instanceof HTMLInputElement) &&
        !(event.target instanceof HTMLTextAreaElement)
      ) {
        event.preventDefault()
        event.stopPropagation()
        setOpen(true)
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [context])

  const toggleDocked = () => {
    setDocked((prev) => {
      const next = !prev
      try {
        localStorage.setItem(DOCK_STORAGE_KEY, next ? '1' : '0')
      } catch {
        // ignore
      }
      if (next) {
        setOpenState(true)
        persistOpenPreference(true)
      }
      return next
    })
  }

  const handleNotesPaginatorClick = (page: number): void => {
    setCurrentNotesPage(page)
  }

  const handleCallsPaginatorClick = (page: number): void => {
    setCurrentCallsPage(page)
  }

  const handleTicketsPaginatorClick = (page: number): void => {
    setCurrentTicketsPage(page)
  }

  const handleSortDirectionChange = (): void => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    setSortDirection(newDirection)
    setCurrentSort(newDirection)
    localStorage.setItem('noteSortDirection', newDirection)
  }

  const handleNoteUpdate = async (noteCuid: string, body: string, tags: string[]) => {
    await dispatch(updateNote({ noteCuid, body, tags }))
  }

  const handleNoteTogglePin = async (note: Note) => {
    await dispatch(updateNote({ noteCuid: note.cuid, isPinned: !note.isPinned }))
  }

  const handleNoteDelete = async (note: Note) => {
    try {
      await new Promise((resolve, reject) => {
        setConfirmModal(<Modal resolve={resolve} reject={reject} />)
      })
      setConfirmModal(false)
      await dispatch(deleteNote({ noteCuid: note.cuid }))
    } catch {
      setConfirmModal(false)
    }
  }

  if (!showDrawerChrome) {
    return null
  }

  const hasSourceEntity = sourceEntity != null && sourceEntity.length > 0
  const isLoadingContext = displayContext == null || !hasSourceEntity
  const totalBadgeCount = notesTotal + callsTotal + ticketsTotal
  const drawerTitle = hasSourceEntity ? formatDrawerTitle(sourceEntity) : 'Notes'

  return (
    <>
      {confirmModal}
      {docked && open && (
        <div
          className="h-screen shrink-0 transition-[width] duration-300 ease-in-out"
          style={{ width: drawerWidth }}
          aria-hidden
        />
      )}
      <div className="fixed top-6 z-40" style={{ right: rightOffset + 24 }}>
        {context != null && (
        <Tooltip content="Notes (n)" placement="left" className={NOTES_TOOLTIP_CLASS} theme={NOTES_TOOLTIP_THEME}>
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="Shortcut: n"
            aria-label="Open notes"
            className="relative flex h-12 w-12 items-center justify-center rounded-full bg-indigo-500 p-2 shadow-lg transition-all duration-200 ease-out hover:scale-105 hover:bg-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 active:scale-95 dark:bg-indigo-700 dark:hover:bg-indigo-600"
          >
            <PencilSquareIcon className="size-5 text-white" />
            {totalBadgeCount > 0 && noteRequestPhase.kind !== 'in-progress' && (
              <>
                <span className="absolute top-8 right-8 flex h-6 min-h-6 w-6 min-w-6 animate-ping-slow items-center justify-center rounded-full bg-slate-400 p-0.5 opacity-75 dark:bg-slate-600" />
                <span className="absolute top-8 right-8 flex h-6 min-h-6 w-6 min-w-6 items-center justify-center rounded-full border border-slate-700 bg-slate-600 p-0.5 text-sm font-semibold text-white dark:border-slate-600 dark:bg-slate-700 dark:text-white">
                  {totalBadgeCount}
                </span>
              </>
            )}
          </button>
        </Tooltip>
        )}
      </div>

      <Transition show={open}>
        <div
          className="pointer-events-none fixed inset-y-0 z-50 flex max-w-full transition duration-300 ease-in-out data-[closed]:translate-x-full"
          style={{ right: rightOffset }}
        >
          <div
            role="dialog"
            aria-modal="false"
            aria-label={drawerTitle}
            className={`pointer-events-auto relative flex h-full min-w-0 max-w-full flex-col overflow-x-hidden bg-slate-50 dark:bg-slate-800${
              docked ? '' : ' shadow-xl shadow-slate-400 dark:shadow-slate-950'
            }`}
            style={{ width: drawerWidth }}
          >
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize notes drawer"
              title="Drag to resize"
              onPointerDown={onResizePointerDown}
              className="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize touch-none bg-transparent transition-colors duration-150 hover:bg-indigo-400/40 active:bg-indigo-500/50"
            />
            <div ref={panelRef} className="flex h-full flex-col overflow-y-scroll">
              <div className="bg-indigo-500 px-4 py-4 dark:bg-indigo-800 sm:px-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="m-0 text-base font-semibold text-white dark:text-white">{drawerTitle}</h2>
                    {label && (
                      <div className="truncate text-sm font-normal text-white dark:text-indigo-300">{label}</div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Tooltip
                      content={docked ? 'Float over page' : 'Dock to side'}
                      placement="bottom"
                      className={NOTES_TOOLTIP_CLASS}
                      theme={NOTES_TOOLTIP_THEME}
                    >
                      <button
                        type="button"
                        onClick={toggleDocked}
                        aria-label={docked ? 'Float over page' : 'Dock to side'}
                        aria-pressed={docked}
                        className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-indigo-200 transition-colors duration-150 hover:bg-indigo-400/40 hover:text-white dark:text-indigo-200 dark:hover:bg-indigo-700 dark:hover:text-white ${
                          docked ? 'bg-indigo-400/40 text-white dark:bg-indigo-700 dark:text-white' : ''
                        }`}
                      >
                        <ViewColumnsIcon className="h-4 w-4" />
                      </button>
                    </Tooltip>
                    <Tooltip
                      content="Hide notes (Esc)"
                      placement="bottom"
                      className={NOTES_TOOLTIP_CLASS}
                      theme={NOTES_TOOLTIP_THEME}
                    >
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        aria-label="Hide notes"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-indigo-200 transition-colors duration-150 hover:bg-indigo-400/40 hover:text-white dark:text-indigo-200 dark:hover:bg-indigo-700 dark:hover:text-white"
                      >
                        <ChevronDoubleRightIcon className="h-4 w-4" />
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {tenantCuid && (
                  <div className="mt-3 flex space-x-1 rounded-lg bg-indigo-600 p-1 dark:bg-indigo-900">
                    <button
                      type="button"
                      onClick={() => setActiveTab('notes')}
                      className={`flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:gap-1.5 sm:text-sm ${
                        activeTab === 'notes'
                          ? 'bg-white text-indigo-700 dark:bg-slate-700 dark:text-white'
                          : 'text-indigo-100 hover:bg-indigo-500 dark:text-indigo-300 dark:hover:bg-indigo-700'
                      }`}
                    >
                      <PencilSquareIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">Notes</span>
                      {notesTotal > 0 && (
                        <span
                          className={`shrink-0 rounded-full px-1.5 py-0.5 text-xs ${
                            activeTab === 'notes'
                              ? 'bg-indigo-100 text-indigo-700 dark:bg-slate-600 dark:text-white'
                              : 'bg-indigo-500 text-white dark:bg-indigo-700'
                          }`}
                        >
                          {notesTotal}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('calls')}
                      className={`flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:gap-1.5 sm:text-sm ${
                        activeTab === 'calls'
                          ? 'bg-white text-indigo-700 dark:bg-slate-700 dark:text-white'
                          : 'text-indigo-100 hover:bg-indigo-500 dark:text-indigo-300 dark:hover:bg-indigo-700'
                      }`}
                    >
                      <PhoneIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">Calls</span>
                      {callsTotal > 0 && (
                        <span
                          className={`shrink-0 rounded-full px-1.5 py-0.5 text-xs ${
                            activeTab === 'calls'
                              ? 'bg-indigo-100 text-indigo-700 dark:bg-slate-600 dark:text-white'
                              : 'bg-indigo-500 text-white dark:bg-indigo-700'
                          }`}
                        >
                          {callsTotal}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('tickets')}
                      className={`flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:gap-1.5 sm:text-sm ${
                        activeTab === 'tickets'
                          ? 'bg-white text-indigo-700 dark:bg-slate-700 dark:text-white'
                          : 'text-indigo-100 hover:bg-indigo-500 dark:text-indigo-300 dark:hover:bg-indigo-700'
                      }`}
                    >
                      <TicketIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">Tickets</span>
                      {ticketsTotal > 0 && (
                        <span
                          className={`shrink-0 rounded-full px-1.5 py-0.5 text-xs ${
                            activeTab === 'tickets'
                              ? 'bg-indigo-100 text-indigo-700 dark:bg-slate-600 dark:text-white'
                              : 'bg-indigo-500 text-white dark:bg-indigo-700'
                          }`}
                        >
                          {ticketsTotal}
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="relative flex-1 px-4 py-6 text-sm text-slate-900 dark:text-slate-100">
                {isLoadingContext ? (
                  <div className="py-8 text-center text-slate-500 dark:text-slate-400">Loading notes…</div>
                ) : drawerTab === 'notes' ? (
                  <>
                    <PanelNotes
                      noteInput={postNoteBody}
                      notes={notes}
                      noteRequestPhase={noteRequestPhase}
                      setNoteInput={setPostNoteBody}
                      onPost={async (entity, body) => {
                        await dispatch(addNote({ sourceEntity: entity, body }))
                        await dispatch(
                          getNotes({
                            sourceEntity: entity,
                            resultsPerPage: 10,
                            page: currentNotesPage,
                            sortCreatedAt: currentSort,
                          })
                        )
                      }}
                      onUpdate={handleNoteUpdate}
                      onTogglePin={handleNoteTogglePin}
                      onDelete={handleNoteDelete}
                      currentUserId={user?.email ?? ''}
                      sourceEntity={sourceEntity!}
                      sortDirection={sortDirection}
                      onSortDirectionChange={handleSortDirectionChange}
                      totalNoteCount={notesTotal}
                    />

                    {notesTotal > notesResultsPerPage && (
                      <Paginator
                        className="mt-4"
                        data={{ total: notesTotal, resultsPerPage: notesResultsPerPage, page: currentNotesPage }}
                        handlePaginatorClick={handleNotesPaginatorClick}
                        isCompact
                      />
                    )}
                  </>
                ) : drawerTab === 'calls' ? (
                  <>
                    <CallsPanel
                      calls={calls}
                      callRequestPhase={callRequestPhase}
                      totalCallCount={callsTotal}
                    />

                    {callsTotal > callsResultsPerPage && (
                      <Paginator
                        className="mt-4"
                        data={{ total: callsTotal, resultsPerPage: callsResultsPerPage, page: currentCallsPage }}
                        handlePaginatorClick={handleCallsPaginatorClick}
                        isCompact
                      />
                    )}
                  </>
                ) : (
                  <>
                    <TicketsPanel
                      tickets={tickets}
                      ticketRequestPhase={ticketRequestPhase}
                      totalTicketCount={ticketsTotal}
                    />

                    {ticketsTotal > ticketsResultsPerPage && (
                      <Paginator
                        className="mt-4"
                        data={{ total: ticketsTotal, resultsPerPage: ticketsResultsPerPage, page: currentTicketsPage }}
                        handlePaginatorClick={handleTicketsPaginatorClick}
                        isCompact
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </>
  )
}

export default NotesDrawer
